import { useEffect, useState } from "react";

export type DashboardSectionKey =
  "reports" | "matrices" | "templates" | "resources";
export type DashboardSectionView = "table" | "carousel";

type DashboardViewPreferences = Record<
  DashboardSectionKey,
  DashboardSectionView
>;

const STORAGE_KEY = "researchly:dashboard:view-preferences";

const DEFAULT_PREFERENCES: DashboardViewPreferences = {
  reports: "table",
  matrices: "table",
  templates: "table",
  resources: "table",
};

function readPreferences(): DashboardViewPreferences {
  if (typeof window === "undefined") return DEFAULT_PREFERENCES;

  try {
    const rawValue = window.localStorage.getItem(STORAGE_KEY);
    if (!rawValue) return DEFAULT_PREFERENCES;

    const parsed = JSON.parse(rawValue) as Partial<DashboardViewPreferences>;
    return {
      matrices:
        parsed.matrices === "carousel" || parsed.matrices === "table"
          ? parsed.matrices
          : DEFAULT_PREFERENCES.matrices,
      reports:
        parsed.reports === "carousel" || parsed.reports === "table"
          ? parsed.reports
          : DEFAULT_PREFERENCES.reports,
      templates:
        parsed.templates === "carousel" || parsed.templates === "table"
          ? parsed.templates
          : DEFAULT_PREFERENCES.templates,
      resources:
        parsed.resources === "carousel" || parsed.resources === "table"
          ? parsed.resources
          : DEFAULT_PREFERENCES.resources,
    };
  } catch {
    return DEFAULT_PREFERENCES;
  }
}

export function useDashboardViewPreferences() {
  const [preferences, setPreferences] =
    useState<DashboardViewPreferences>(readPreferences);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
  }, [preferences]);

  function setView(section: DashboardSectionKey, view: DashboardSectionView) {
    setPreferences((current) => ({
      ...current,
      [section]: view,
    }));
  }

  return {
    preferences,
    setView,
  };
}
